import { Box, ButtonBase, Stack, Typography, SxProps, Theme } from '@mui/material';
import { SystemStyleObject } from '@mui/system';

type DayPickerVariant = 'default' | 'red';

const VARIANT_COLORS = {
  default: {
    label: 'common.grey2',
    selectedLabel: 'primary.main',
    topLabel: 'common.grey',
    selectedTopLabel: 'common.grey2',
  },
  red: {
    label: 'common.grey',
    selectedLabel: 'common.grey3',
    topLabel: 'background.button.error.hover',
    selectedTopLabel: 'error.main',
  },
};

export interface DayPickerOption<T> {
  value: T;
  topLabel?: string;
  label: string;
  variant?: DayPickerVariant;
  disabled?: boolean;
}

interface DayPickerProps<T> {
  days: DayPickerOption<T>[];
  selectedValue?: T | null;
  onSelect: (value: T) => void;
  isEqual?: (a: T, b: T) => boolean;
  shouldShowIndicator?: (value: T) => boolean;
  buttonSx?: SxProps<Theme>;
}

export const DayPicker = <T,>({
  days,
  selectedValue,
  onSelect,
  isEqual = (a: T, b: T) => a === b,
  shouldShowIndicator,
  buttonSx,
}: DayPickerProps<T>) => {
  return (
    <Stack direction="row" justifyContent="space-between">
      {days.map((day) => {
        const isSelected = !!selectedValue && isEqual(day.value, selectedValue);
        const showIndicator = shouldShowIndicator?.(day.value);
        const colors = VARIANT_COLORS[day.variant ?? 'default'];

        const activeStyles = {
          backgroundColor: 'common.lightBg',
          '& .topLabel': { color: colors.selectedTopLabel },
          '& .label': { color: colors.selectedLabel },
        };

        return (
          <Stack key={String(day.value)} alignItems="center" gap={0.5}>
            <ButtonBase
              disabled={day.disabled}
              onClick={() => onSelect(day.value)}
              sx={(theme) => ({
                display: 'flex',
                minWidth: 30,
                width: '100%',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                padding: theme.spacing(0.5, 1),
                gap: 0.75,
                borderRadius: theme.spacing(1),
                cursor: day.disabled ? 'default' : 'pointer',
                opacity: day.disabled ? 0.4 : 1,
                ...(isSelected && activeStyles),
                '&:hover': activeStyles,
                ...((typeof buttonSx === 'function'
                  ? buttonSx(theme)
                  : buttonSx) as SystemStyleObject),
              })}
            >
              {day.topLabel && (
                <Typography className="topLabel" color={colors.topLabel}>
                  {day.topLabel}
                </Typography>
              )}
              <Typography className="label" color={colors.label}>
                {day.label}
              </Typography>
            </ButtonBase>
            {showIndicator && (
              <Box
                sx={{
                  width: 4,
                  height: 4,
                  borderRadius: 100,
                  backgroundColor: isSelected ? 'primary.main' : 'common.grey2',
                }}
              />
            )}
          </Stack>
        );
      })}
    </Stack>
  );
};
